import axiosInstance from "../../scenes/utils/axiosInstance";
import tenantAxiosInstance from "../../scenes/utils/tenantAxiosInstance";
import { API_ENDPOINTS } from "../../config/api";

export default class StatsService {
  /**
   * Genel stats API çağrısı
   * @param {Object} params - Query parametreleri
   * @param {string} params.entity - Entity tipi (patients, forms, form_answers, form_invitations, files, scoring_formulas)
   * @param {string} [params.metric] - Metrik tipi (count, new_count, avg_score, vb.)
   * @param {string} [params.start_date] - Başlangıç tarihi (YYYY-MM-DD)
   * @param {string} [params.end_date] - Bitiş tarihi (YYYY-MM-DD)
   * @param {string} [params.group_by] - Gruplama tipi (none, day, week, month, quarter, year)
   * @param {Object|string} [params.filters] - Filtreler (object veya JSON string)
   * @param {Object|string} [params.compare_with] - Karşılaştırma parametreleri (object veya JSON string)
   * @param {string} [params.order_by] - Sıralama alanı (date, value)
   * @param {string} [params.order] - Sıralama yönü (asc, desc)
   * @param {number} [params.limit] - Sonuç limiti
   * @returns {Promise<Object>} Stats response
   */
  getStats = async (params = {}) => {
    try {
      // filters ve compare_with object ise JSON string'e çevir
      const queryParams = { ...params };
      
      if (queryParams.filters && typeof queryParams.filters === 'object') {
        queryParams.filters = JSON.stringify(queryParams.filters);
      }
      
      if (queryParams.compare_with && typeof queryParams.compare_with === 'object') {
        queryParams.compare_with = JSON.stringify(queryParams.compare_with);
      }

      const response = await axiosInstance.get(API_ENDPOINTS.STATS.GENERAL, {
        params: queryParams
      });
      
      return response.data;
    } catch (error) {
      console.error("Stats verileri alınırken bir hata oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Doktor stats API çağrısı
   * @param {Object} params - Query parametreleri (getStats ile aynı)
   * @returns {Promise<Object>} Stats response
   */
  getDoctorStats = async (params = {}) => {
    try {
      const queryParams = { ...params };
      
      if (queryParams.filters && typeof queryParams.filters === 'object') {
        queryParams.filters = JSON.stringify(queryParams.filters);
      }
      
      if (queryParams.compare_with && typeof queryParams.compare_with === 'object') {
        queryParams.compare_with = JSON.stringify(queryParams.compare_with);
      }

      const response = await axiosInstance.get(API_ENDPOINTS.STATS.DOCTOR, {
        params: queryParams
      });
      
      return response.data;
    } catch (error) {
      console.error("Doktor stats verileri alınırken bir hata oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Kurum (Tenant) stats API çağrısı
   * @param {Object} params - Query parametreleri (getStats ile aynı)
   * @returns {Promise<Object>} Stats response
   */
  getTenantStats = async (params = {}) => {
    try {
      const queryParams = { ...params };
      
      if (queryParams.filters && typeof queryParams.filters === 'object') {
        queryParams.filters = JSON.stringify(queryParams.filters);
      }
      
      if (queryParams.compare_with && typeof queryParams.compare_with === 'object') {
        queryParams.compare_with = JSON.stringify(queryParams.compare_with);
      }

      const response = await tenantAxiosInstance.get(API_ENDPOINTS.STATS.TENANT, {
        params: queryParams
      });
      
      return response.data;
    } catch (error) {
      console.error("Kurum stats verileri alınırken bir hata oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Super Admin stats API çağrısı
   * @param {Object} params - Query parametreleri (getStats ile aynı)
   * @returns {Promise<Object>} Stats response
   */
  getSuperAdminStats = async (params = {}) => {
    try {
      const queryParams = { ...params };
      
      if (queryParams.filters && typeof queryParams.filters === 'object') {
        queryParams.filters = JSON.stringify(queryParams.filters);
      }
      
      if (queryParams.compare_with && typeof queryParams.compare_with === 'object') {
        queryParams.compare_with = JSON.stringify(queryParams.compare_with);
      }

      const response = await axiosInstance.get(API_ENDPOINTS.STATS.SUPER_ADMIN, {
        params: queryParams
      });
      
      return response.data;
    } catch (error) {
      console.error("Super admin stats verileri alınırken bir hata oluştu:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Helper: Tarih aralığı hesaplama
   * @param {string} period - Zaman periyodu (last_week, last_month, last_year, vb.)
   * @returns {Object} { startDate, endDate } - ISO formatında tarihler
   */
  getDateRange = (period) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (period) {
      case 'last_week':
        // Bugün dahil son 7 gün (bugün + son 6 gün = 7 gün)
        startDate.setDate(endDate.getDate() - 6);
        break;
      case 'last_2_weeks':
        // Bugün dahil son 14 gün (bugün + son 13 gün = 14 gün)
        startDate.setDate(endDate.getDate() - 13);
        break;
      case 'last_month':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case 'last_3_months':
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case 'last_6_months':
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case 'last_year':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      case 'this_month':
        startDate.setDate(1);
        break;
      case 'last_month_period':
        startDate.setMonth(endDate.getMonth() - 1);
        startDate.setDate(1);
        endDate.setDate(0); // Geçen ayın son günü
        break;
      case 'this_year':
        startDate.setMonth(0);
        startDate.setDate(1);
        break;
      case 'last_year_period':
        startDate.setFullYear(endDate.getFullYear() - 1);
        startDate.setMonth(0);
        startDate.setDate(1);
        endDate.setFullYear(endDate.getFullYear() - 1);
        endDate.setMonth(11);
        endDate.setDate(31);
        break;
      default:
        // Varsayılan: Son 1 ay
        startDate.setMonth(endDate.getMonth() - 1);
    }

    // Start date'i günün başına ayarla (00:00:00) - local time
    startDate.setHours(0, 0, 0, 0);
    
    // End date'i günün sonuna ayarla (23:59:59) - bugün oluşturulan kayıtlar dahil olsun - local time
    endDate.setHours(23, 59, 59, 999);
    
    // Local time'ı kullanarak YYYY-MM-DD formatında döndür (timezone sorunlarını önlemek için)
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate)
    };
  };

  /**
   * Helper: Zaman periyoduna göre önerilen group_by değeri
   * @param {string} period - Zaman periyodu
   * @returns {string} Önerilen group_by değeri
   */
  getSuggestedGroupBy = (period) => {
    switch (period) {
      case 'last_week':
      case 'last_2_weeks':
        return 'day';
      case 'last_month':
        return 'day';
      case 'last_3_months':
        return 'week';
      case 'last_6_months':
        return 'month';
      case 'last_year':
        return 'month';
      case 'this_year':
      case 'last_year_period':
        return 'quarter';
      case 'custom':
        return 'day'; // Custom date range için varsayılan olarak 'day'
      default:
        return 'day';
    }
  };
}
